<template>
  <ClientOnly>
    <!-- Demo Alert - Only when in demo mode -->
    <div 
      v-if="showDemoAlert"
      class="fixed bottom-3 left-1/2 -translate-x-1/2 z-[99] max-w-[calc(100vw-1.5rem)] cursor-pointer transition-all duration-300 ease-out sm:bottom-4 sm:max-w-[min(90vw,400px)]"
      :class="{ 'expanded': isExpanded }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @click="handleClick"
      @touchstart="handleTouchStart"
      @touchend="handleTouchEnd"
    >
      <div class="bg-gradient-to-br from-amber-400 to-amber-500 border-2 border-amber-600 rounded-full hover:rounded-lg shadow-[0_10px_25px_-5px_rgba(0,0,0,0.2),0_8px_10px_-6px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300 ease-out hover:shadow-[0_20px_35px_-5px_rgba(0,0,0,0.3),0_10px_15px_-6px_rgba(0,0,0,0.15)] hover:-translate-y-0.5"
           :class="{ 'expanded-content': isExpanded }">
        <div class="px-3.5 py-2.5 flex items-center justify-center gap-2 sm:px-4 sm:py-3">
          <span class="text-sm leading-none sm:text-base">⚠️</span>
          <span class="text-[0.6875rem] font-bold tracking-wider text-amber-900 uppercase font-sans sm:text-xs">DEMO</span>
        </div>
        <transition name="expand">
          <div v-if="isExpanded" class="px-4 py-3 bg-white/10 border-t border-amber-600/30 sm:px-5 sm:py-3.5">
            <p class="text-[0.8125rem] font-semibold text-amber-900 mb-1.5 leading-[1.4] sm:text-sm">
              {{ t('common.demoAlert.message') }}
            </p>
            <p class="text-[0.6875rem] text-amber-800 leading-[1.4] sm:text-xs">
              {{ t('common.demoAlert.event') }}
            </p>
          </div>
        </transition>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from '#imports'
import { useArtists } from '~/composables/useArtists'
import { useConsent } from '~/composables/useConsent'

const { t } = useI18n()
const { dataSource, dataReason } = useArtists()
const route = useRoute()

const isExpanded = ref(false)
const isCollectionInViewport = ref(false)
const intersectionObserver = ref<IntersectionObserver | null>(null)
const isTouchDevice = ref(false)
const touchStartTime = ref<number | null>(null)

// Check if we're on homepage (language-sensitive)
const isHomepage = computed(() => {
  const path = route.path
  // Homepage can be / or /{locale} where locale is 2 letters
  return path === '/' || /^\/[a-z]{2}$/.test(path)
})

// Check if route is allowed (language-sensitive)
const isAllowedRoute = computed(() => {
  const path = route.path
  
  // Homepage
  if (isHomepage.value) {
    return true
  }
  
  // Remove locale prefix for matching (e.g., /de/collection -> /collection)
  const pathWithoutLocale = path.replace(/^\/[a-z]{2}(\/|$)/, (match, p1) => p1 || '/')
  
  // Collection routes: /collection, /collection/*, or /{locale}/collection, /{locale}/collection/*
  if (pathWithoutLocale === '/collection' || /^\/collection(\/|$)/.test(pathWithoutLocale)) {
    return true
  }
  
  // Humans route: /humans or /{locale}/humans
  if (pathWithoutLocale === '/humans') {
    return true
  }
  
  // Artist/Provider route: /of/[slug] or /{locale}/of/[slug]
  if (/^\/of\//.test(pathWithoutLocale)) {
    return true
  }
  
  return false
})

// Check if we're on an artist page (/of/[slug])
const isArtistPage = computed(() => {
  const path = route.path
  // Remove locale prefix for matching
  const pathWithoutLocale = path.replace(/^\/[a-z]{2}(\/|$)/, (match, p1) => p1 || '/')
  return /^\/of\//.test(pathWithoutLocale)
})

const showDemoAlert = computed(() => {
  // Only show if in mock data mode
  if (dataSource.value !== 'fallback' || dataReason.value !== 'use_mock_data') {
    return false
  }
  
  // Only show on allowed routes
  if (!isAllowedRoute.value) {
    return false
  }
  
  // On homepage, only show when collection is in viewport
  if (isHomepage.value) {
    return isCollectionInViewport.value
  }
  
  // Explicitly show on artist pages (/of/[slug]) when using mock data
  if (isArtistPage.value) {
    return true
  }
  
  // On other allowed routes, show normally
  return true
})

const setupIntersectionObserver = () => {
  if (typeof document === 'undefined') return
  
  // Clean up existing observer
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect()
    intersectionObserver.value = null
  }
  
  // Only set up observer on homepage
  if (!isHomepage.value) {
    return
  }
  
  // Wait a bit for the DOM to be ready
  setTimeout(() => {
    const collectionSection = document.querySelector('[data-collection-section]')
    if (!collectionSection) {
      // Retry after a short delay if element not found
      setTimeout(() => setupIntersectionObserver(), 500)
      return
    }
    
    intersectionObserver.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isCollectionInViewport.value = entry.isIntersecting
        })
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    )
    
    intersectionObserver.value.observe(collectionSection)
  }, 100)
}

// Watch route changes to set up observer
watch(() => route.path, () => {
  isCollectionInViewport.value = false
  setupIntersectionObserver()
}, { immediate: false })

const positionChatwootLeft = () => {
  if (typeof document === 'undefined') return
  
  // Position Chatwoot widget on the left side (bottom-left)
  // Only target the bubble holder, and handle widget window separately
  const style = document.createElement('style')
  style.id = 'chatwoot-left-position'
  style.textContent = `
    /* Position the bubble holder (floating button) on the left - bottom-left */
    /* Set z-index lower than consent banner (z-50) - must be below 50 */
    .woot--bubble-holder,
    [class*="woot--bubble"],
    [id*="woot-bubble"],
    [id*="woot-bubble"] * {
      right: auto;
      left: 1rem;
      bottom: 1rem;
      z-index: 40;
    }
    
    /* Position the main widget holder container on the left (for bubble positioning) */
    #woot-widget-holder,
    [id*="woot-widget"],
    #woot-widget-holder *,
    [id*="woot-widget"] * {
      right: auto;
      left: auto;
      bottom: 1rem;
      z-index: 40;
    }
    
    /* Widget holder class variant */
    .woot-widget-holder,
    [class*="woot-widget-holder"],
    .woot-widget-holder *,
    [class*="woot-widget-holder"] * {
      right: auto;
      left: auto;
      bottom: 1rem;
      z-index: 40;
    }
    
    /* Ensure bubble button is positioned bottom-left */
    /* Set z-index lower than consent banner for all bubble elements */
    .woot--bubble-holder .woot-widget-bubble,
    .woot--bubble-holder button,
    #woot-widget-holder .woot-widget-bubble,
    #woot-widget-holder button,
    .woot-widget-bubble,
    [class*="woot-widget-bubble"] {
      right: auto;
      left: 1rem;
      z-index: 40;
    }
    
    /* Target all Chatwoot-specific elements to ensure lower z-index */
    /* More specific selectors to catch all Chatwoot widget elements */
    div[id*="woot"],
    div[class*="woot-widget"],
    div[class*="woot--bubble"],
    iframe[id*="woot"],
    iframe[class*="woot"] {
      z-index: 40;
    }
    
    /* On mobile, ensure widget window opens properly */
    @media (max-width: 768px) {
      /* When widget is open, reset positioning so window can be full-width */
      /* Keep z-index lower than consent banner even when open */
      #woot-widget-holder.woot-widget-holder--open,
      #woot-widget-holder[data-state="open"],
      #woot-widget-holder[aria-expanded="true"] {
        left: 0;
        right: 0;
        width: 100vw;
        max-width: 100vw;
        bottom: 0;
        z-index: 40;
      }
      
      /* Widget window container - ensure it's full-width when open */
      #woot-widget-holder.woot-widget-holder--open .woot-widget-container,
      #woot-widget-holder.woot-widget-holder--open .woot--widget-container,
      #woot-widget-holder[data-state="open"] .woot-widget-container,
      #woot-widget-holder[data-state="open"] .woot--widget-container,
      #woot-widget-holder[aria-expanded="true"] .woot-widget-container,
      #woot-widget-holder[aria-expanded="true"] .woot--widget-container {
        left: 0;
        right: 0;
        width: 100%;
        max-width: 100%;
      }
      
      /* Widget iframe when open */
      #woot-widget-holder.woot-widget-holder--open iframe,
      #woot-widget-holder[data-state="open"] iframe,
      #woot-widget-holder[aria-expanded="true"] iframe {
        width: 100%;
        max-width: 100%;
      }
      
      /* Keep bubble on left even when widget is open */
      /* Ensure z-index is lower than consent banner */
      #woot-widget-holder .woot--bubble-holder,
      #woot-widget-holder[data-state="open"] .woot--bubble-holder,
      #woot-widget-holder[aria-expanded="true"] .woot--bubble-holder {
        left: 0;
        right: auto;
        bottom: 1rem;
        z-index: 40;
      }
    }
  `
  // Update existing style or add new one
  const existingStyle = document.getElementById('chatwoot-left-position')
  if (existingStyle) {
    existingStyle.textContent = style.textContent
  } else {
    document.head.appendChild(style)
  }
}

const initChatwoot = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return

  // Position widget on left immediately
  positionChatwootLeft()

  // Check if Chatwoot is already loaded
  if (window.chatwootSDK) {
    // Wait a bit for widget to render, then position it
    setTimeout(() => {
      positionChatwootLeft()
    }, 500)
    return
  }

  // Load Chatwoot SDK
  const BASE_URL = 'https://app.chatwoot.com'
  const script = document.createElement('script')
  script.src = `${BASE_URL}/packs/js/sdk.js`
  script.async = true
  
  script.onload = () => {
    if (window.chatwootSDK) {
      window.chatwootSDK.run({
        websiteToken: 'cX8R2NgF5kFGRzPtkGKstwfM',
        baseUrl: BASE_URL
      })
      // Position widget after it loads
      setTimeout(() => {
        positionChatwootLeft()
      }, 1000)
    }
  }
  
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }
}

// Handle mouse events (desktop)
const handleMouseEnter = () => {
  if (!isTouchDevice.value) {
    isExpanded.value = true
  }
}

const handleMouseLeave = () => {
  if (!isTouchDevice.value) {
    isExpanded.value = false
  }
}

// Handle click (desktop toggle)
const handleClick = (e: MouseEvent) => {
  // Only toggle on desktop, not on touch devices
  if (!isTouchDevice.value) {
    isExpanded.value = !isExpanded.value
  }
}

// Handle touch events (mobile)
const handleTouchStart = (e: TouchEvent) => {
  isTouchDevice.value = true
  touchStartTime.value = Date.now()
  isExpanded.value = true
  // Prevent default to avoid triggering click
  e.preventDefault()
}

const handleTouchEnd = (e: TouchEvent) => {
  if (isTouchDevice.value && touchStartTime.value !== null) {
    isExpanded.value = false
    touchStartTime.value = null
  }
}

onMounted(() => {
  // Detect touch device
  if (typeof window !== 'undefined') {
    isTouchDevice.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }
  
  // Set up intersection observer for homepage collection section
  setupIntersectionObserver()
  
  // Check consent before initializing Chatwoot
  const { hasConsent, requireConsent } = useConsent()
  const consentCookie = useCookie<boolean | null>('analytics_consent', { default: () => null })
  
  // Only initialize Chatwoot if:
  // 1. Consent is not required, OR
  // 2. Consent is required and user has granted it (consentCookie.value === true)
  const shouldLoadChatwoot = !requireConsent.value || consentCookie.value === true
  
  if (shouldLoadChatwoot) {
    // Position Chatwoot on left immediately
    positionChatwootLeft()
    // Initialize Chatwoot only when consent is granted
    initChatwoot()
    
    // Watch for Chatwoot elements being added to the DOM
    const observer = new MutationObserver(() => {
      positionChatwootLeft()
    })
    
    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
    
    // Also position after delays to catch widget when it renders
    setTimeout(() => {
      positionChatwootLeft()
    }, 500)
    setTimeout(() => {
      positionChatwootLeft()
    }, 1000)
    setTimeout(() => {
      positionChatwootLeft()
    }, 2000)
    setTimeout(() => {
      positionChatwootLeft()
    }, 3000)
  }
  
  // Watch for consent changes to load Chatwoot when consent is granted
  watch([hasConsent, requireConsent], ([hasConsentValue, requireConsentValue]) => {
    if (requireConsentValue && hasConsentValue && !window.chatwootSDK) {
      // User just granted consent, initialize Chatwoot now
      positionChatwootLeft()
      initChatwoot()
      
      // Set up positioning observer for Chatwoot elements
      const observer = new MutationObserver(() => {
        positionChatwootLeft()
      })
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      })
      
      // Position after delays to catch widget when it renders
      setTimeout(() => positionChatwootLeft(), 500)
      setTimeout(() => positionChatwootLeft(), 1000)
      setTimeout(() => positionChatwootLeft(), 2000)
      setTimeout(() => positionChatwootLeft(), 3000)
    }
  })
})

onUnmounted(() => {
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect()
    intersectionObserver.value = null
  }
})
</script>

<style scoped>
/* Expanded state styles */
.expanded-content {
  box-shadow: 0 20px 35px -5px rgba(0, 0, 0, 0.3), 0 10px 15px -6px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

/* Expand animation - minimal CSS for Vue transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 200px;
  opacity: 1;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
